import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  FileText,
  FileSpreadsheet,
  Share2,
  RotateCcw,
  Filter,
  Calendar,
  Layers,
  Building2,
  Users,
  Truck,
  HardHat,
  Wallet,
  IndianRupee
} from 'lucide-react';
import { getReport, useReport, type ReportFilters, type FilterKey } from '../lib/reports';
import { exportExcel, exportPdf, sharePdf, canShare } from '../lib/export';
import { useLookups } from '../lib/lookups';
import { useClientOptions } from '../lib/queries';
import Shell from '../components/Shell';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  useToast,
  Select,
} from '../components/ui';

export default function ReportView() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const def = getReport(key);
  const toast = useToast();

  const [filters, setFilters] = useState<ReportFilters>({});
  const [ran, setRan] = useState(true);
  const [busy, setBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);

  const { data: lk } = useLookups();
  const { data: clients } = useClientOptions();
  const { data: rows, isLoading, error, refetch } = useReport(key, filters, ran);

  if (!def) {
    return (
      <Shell title="Report" crumb="Insights">
        <div className="card">
          <EmptyState icon="filetext" title="Report definition not found" />
        </div>
      </Shell>
    );
  }

  const set = (k: FilterKey) => (e: { target: { value: string } }) => {
    setFilters((f) => ({ ...f, [k]: e.target.value || undefined }));
    setRan(true);
  };

  /** Human-readable filter summary, printed onto the export. */
  function describeFilters(): string[] {
    const out: string[] = [];
    if (filters.from || filters.to) {
      out.push(`${filters.from || 'start'} to ${filters.to || 'today'}`);
    }
    const name = (list: Array<{ id: string; name: string }> | undefined, id?: string) =>
      list?.find((x) => x.id === id)?.name;
    if (filters.projectId) out.push(`Project: ${name(lk?.projects, filters.projectId) ?? '—'}`);
    if (filters.clientId) out.push(`Client: ${name(clients, filters.clientId) ?? '—'}`);
    if (filters.supplierId) out.push(`Supplier: ${name(lk?.suppliers, filters.supplierId) ?? '—'}`);
    if (filters.subcontractorId) out.push(`Sub-contractor: ${name(lk?.subcontractors, filters.subcontractorId) ?? '—'}`);
    if (filters.workTypeId) out.push(`Trade / Work Type: ${name(lk?.workTypes, filters.workTypeId) ?? '—'}`);
    if (filters.materialId) out.push(`Material: ${name(lk?.materials, filters.materialId) ?? '—'}`);
    if (filters.status) out.push(`Status: ${filters.status}`);
    if (filters.paymentType) out.push(`Payment Type: ${filters.paymentType}`);
    if (filters.direction) out.push(`Direction: ${filters.direction}`);
    if (filters.mode) out.push(`Mode: ${filters.mode}`);
    return out.length ? out : ['No filters — all records'];
  }

  async function runExport(kind: 'excel' | 'pdf' | 'share') {
    if (!rows?.length || !def) return;
    setBusy(kind);
    try {
      const meta = { title: def.title, subtitle: 'VNS Construction ERP', filters: describeFilters() };
      const exportRows = rows.map((r, i) => ({ sno: i + 1, ...r }));
      if (kind === 'excel') {
        await exportExcel(def.columns, exportRows, meta);
        toast.show('Excel report downloaded');
      } else if (kind === 'pdf') {
        await exportPdf(def.columns, exportRows, meta);
        toast.show('PDF report downloaded');
      } else {
        const shared = await sharePdf(def.columns, exportRows, meta);
        if (!shared) toast.show('Sharing is not supported on this device', true);
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not generate report file', true);
    } finally {
      setBusy(null);
    }
  }

  const totals = ran && rows?.length && def.totals ? def.totals(rows) : null;
  const hasRows = !!rows?.length;

  return (
    <Shell title={def.title} crumb="Reports">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <button
          className="btn btn-auto btn-sm btn-ghost"
          style={{
            marginBottom: 16,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--text-soft)',
            paddingLeft: 4,
          }}
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft size={14} />
          <span>Back to Reports Directory</span>
        </button>

        {/* Filter Parameters Form */}
        <div
          className="card"
          style={{
            marginBottom: 20,
            padding: '20px 24px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Filter size={15} style={{ color: 'var(--brand)' }} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              Report Filters & Parameters
            </h3>
          </div>

          <div
            className="form-grid"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
          >
            <div className="field">
              <label>From Date</label>
              <input type="date" value={filters.from ?? ''} onChange={set('from')} />
            </div>

            <div className="field">
              <label>To Date</label>
              <input type="date" value={filters.to ?? ''} onChange={set('to')} />
            </div>

            {def.filters.includes('projectId') && (
              <div className="field">
                <label>Project Site</label>
                <Select
                  value={filters.projectId ?? ''}
                  onChange={(val) => set('projectId')({ target: { value: val } })}
                  placeholder="All Projects"
                  options={[
                    { value: '', label: 'All Projects' },
                    ...(lk?.projects.map((p) => ({ value: p.id, label: p.name })) ?? []),
                  ]}
                />
              </div>
            )}

            {def.filters.includes('paymentType') && (
              <div className="field">
                <label>Payment Category</label>
                <Select
                  value={filters.paymentType ?? ''}
                  onChange={(val) => set('paymentType')({ target: { value: val } })}
                  placeholder="All Payment Categories"
                  options={[
                    { value: '', label: 'All Payment Categories' },
                    { value: 'client', label: 'Client Receipts (In)' },
                    { value: 'supplier', label: 'Supplier Settlements (Out)' },
                    { value: 'purchase_advance', label: 'Purchase Advances (Out)' },
                    { value: 'subcontractor', label: 'Subcontractor Labour (Out)' },
                    { value: 'expense', label: 'Site Expenses (Out)' },
                    { value: 'transfer', label: 'Bank / Cash Fund Transfers' },
                  ]}
                />
              </div>
            )}

            {def.filters.includes('direction') && (
              <div className="field">
                <label>Cash Flow Direction</label>
                <Select
                  value={filters.direction ?? ''}
                  onChange={(val) => set('direction')({ target: { value: val } })}
                  placeholder="All Cash Flows"
                  options={[
                    { value: '', label: 'All Cash Flows' },
                    { value: 'Received', label: 'Money In (Receipts)' },
                    { value: 'Paid', label: 'Money Out (Disbursements)' },
                    { value: 'Transfer', label: 'Internal Fund Transfers' },
                  ]}
                />
              </div>
            )}

            {def.filters.includes('mode') && (
              <div className="field">
                <label>Payment Mode</label>
                <Select
                  value={filters.mode ?? ''}
                  onChange={(val) => set('mode')({ target: { value: val } })}
                  placeholder="All Payment Modes"
                  options={[
                    { value: '', label: 'All Payment Modes' },
                    { value: 'CASH', label: 'Cash' },
                    { value: 'UPI', label: 'UPI' },
                    { value: 'NEFT', label: 'NEFT' },
                    { value: 'RTGS', label: 'RTGS' },
                    { value: 'CHEQUE', label: 'Cheque' },
                    { value: 'DIRECT', label: 'Direct / Bank' },
                    { value: 'INTERNAL', label: 'Internal Transfer' },
                  ]}
                />
              </div>
            )}

            {def.filters.includes('clientId') && (
              <div className="field">
                <label>Client</label>
                <Select
                  value={filters.clientId ?? ''}
                  onChange={(val) => set('clientId')({ target: { value: val } })}
                  placeholder="All Clients"
                  options={[
                    { value: '', label: 'All Clients' },
                    ...(clients?.map((c) => ({ value: c.id, label: c.name })) ?? []),
                  ]}
                />
              </div>
            )}

            {def.filters.includes('supplierId') && (
              <div className="field">
                <label>Supplier / Vendor</label>
                <Select
                  value={filters.supplierId ?? ''}
                  onChange={(val) => set('supplierId')({ target: { value: val } })}
                  placeholder="All Suppliers"
                  options={[
                    { value: '', label: 'All Suppliers' },
                    ...(lk?.suppliers.map((s) => ({ value: s.id, label: s.name })) ?? []),
                  ]}
                />
              </div>
            )}

            {def.filters.includes('subcontractorId') && (
              <div className="field">
                <label>Labour Contractor</label>
                <Select
                  value={filters.subcontractorId ?? ''}
                  onChange={(val) => set('subcontractorId')({ target: { value: val } })}
                  placeholder="All Sub-contractors"
                  options={[
                    { value: '', label: 'All Sub-contractors' },
                    ...(lk?.subcontractors.map((s) => ({ value: s.id, label: s.name })) ?? []),
                  ]}
                />
              </div>
            )}

            {def.filters.includes('workTypeId') && (
              <div className="field">
                <label>Trade / Specialisation</label>
                <Select
                  value={filters.workTypeId ?? ''}
                  onChange={(val) => set('workTypeId')({ target: { value: val } })}
                  placeholder="All Trades"
                  options={[
                    { value: '', label: 'All Trades' },
                    ...(lk?.workTypes.map((w) => ({ value: w.id, label: w.name })) ?? []),
                  ]}
                />
              </div>
            )}

            {def.filters.includes('materialId') && (
              <div className="field">
                <label>Material SKU</label>
                <Select
                  value={filters.materialId ?? ''}
                  onChange={(val) => set('materialId')({ target: { value: val } })}
                  placeholder="All Materials"
                  options={[
                    { value: '', label: 'All Materials' },
                    ...(lk?.materials.map((m) => ({ value: m.id, label: m.name })) ?? []),
                  ]}
                />
              </div>
            )}

            {def.filters.includes('status') && (
              <div className="field">
                <label>Project Status</label>
                <Select
                  value={filters.status ?? ''}
                  onChange={(val) => set('status')({ target: { value: val } })}
                  placeholder="All Statuses"
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'ongoing', label: 'Ongoing' },
                    { value: 'on_hold', label: 'On Hold' },
                    { value: 'planning', label: 'Planning' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                />
              </div>
            )}
          </div>

        <div className="report-actions-bar">
          <div className="report-actions-primary">
            <button
              className="btn btn-primary btn-auto btn-sm"
              onClick={() => {
                setRan(true);
                void refetch();
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={14} />
              <span>Generate Report</span>
            </button>

            <button
              className="btn btn-auto btn-sm btn-ghost"
              onClick={() => {
                setFilters({});
                setRan(false);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, borderRadius: 'var(--radius-sm)' }}
            >
              <RotateCcw size={13} />
              <span>Reset Filters</span>
            </button>
          </div>

          <div className="report-actions-exports">
            <button
              className="btn btn-auto btn-sm"
              disabled={!hasRows || !!busy}
              onClick={() => void runExport('excel')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileSpreadsheet size={13} />
              <span>{busy === 'excel' ? 'Exporting…' : 'Export Excel'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm"
              disabled={!hasRows || !!busy}
              onClick={() => void runExport('pdf')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={13} />
              <span>{busy === 'pdf' ? 'Exporting…' : 'Export PDF'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm"
              disabled={!hasRows || !!busy}
              onClick={() => void runExport('share')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Share2 size={13} />
              <span>{busy === 'share' ? 'Sharing…' : 'Share'}</span>
            </button>
          </div>
        </div>
        </div>

        {/* Center-Aligned Results Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {!ran ? (
              <div style={{ padding: '40px 20px' }}>
                <EmptyState
                  icon="filter"
                  title="Choose your filters, then click Generate Report"
                  hint="Leave filter fields blank to include all approved records across the system."
                />
              </div>
            ) : isLoading ? (
              <TableSkeleton rows={6} cols={def.columns.length} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !hasRows ? (
              <div style={{ padding: '40px 20px' }}>
                <EmptyState
                  icon="filetext"
                  title="No records match your filter criteria"
                  hint="Try widening the date range or selecting all projects."
                />
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    {def.columns.map((c) => (
                      <th key={c.key}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      {def.columns.map((c) => (
                        <td
                          key={c.key}
                          className={c.numeric ? 'num' : undefined}
                        >
                          {String(r[c.key] ?? (c.key === 'sno' ? i + 1 : '—'))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr>
                      {def.columns.map((c, i) => (
                        <td
                          key={c.key}
                          className={c.numeric ? 'num' : undefined}
                          style={{ fontWeight: 700 }}
                        >
                          {c.key === 'sno'
                            ? ''
                            : i === 1 || (i === 0 && def.columns[0]?.key !== 'sno')
                            ? 'Summary Totals'
                            : totals[c.key] ?? ''}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>

        {ran && hasRows && (
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-faint)', textAlign: 'center' }}>
            Showing {rows.length} record{rows.length === 1 ? '' : 's'} · Verified & approved entries only
          </p>
        )}
      </motion.div>
    </Shell>
  );
}
